import { useTranslation } from 'react-i18next';
import { IconX, IconExternalLink } from '@tabler/icons-react';
import { TeeButton } from '@/components/ui/tee-button';
import { TeeVideoPlayer } from '@/components/ui/tee-video-player';
import { ContentItem, ContentMediaType, VideoProcessingStatus } from '@/types/contentItem';
import { getEmbedIframeSrc } from '@/utils/embedUrl.utils';

export interface ContentItemPreviewModalProps {
  isOpen: boolean;
  item: ContentItem | null;
  onClose: () => void;
}

export default function ContentItemPreviewModal({
  isOpen,
  item,
  onClose,
}: ContentItemPreviewModalProps) {
  const { t } = useTranslation();

  if (!isOpen || !item) return null;

  const imageUrl = item.imageSignedUrl ?? item.imageUrl;
  const embedIframeSrc =
    item.embedUrl && item.embedProvider
      ? getEmbedIframeSrc(item.embedUrl, item.embedProvider)
      : null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={onClose}
    >
      <div
        className="mx-4 w-full max-w-2xl overflow-hidden rounded-[10px] border border-[var(--border-default)] bg-[var(--background-page-content)] shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-4 border-b border-[var(--border-default)] px-6 py-4">
          <h2 className="truncate text-heading-h3 font-normal text-[var(--text-content-title)]">
            {item.title}
          </h2>
          <button
            type="button"
            aria-label={t('common.close')}
            onClick={onClose}
            className="shrink-0 text-[var(--text-content-sub-title)]"
          >
            <IconX size={24} />
          </button>
        </div>

        <div className="flex items-center justify-center bg-[var(--background-page-header)] p-6">
          {item.mediaType === ContentMediaType.IMAGE &&
            (imageUrl ? (
              <img
                src={imageUrl}
                alt={item.title}
                className="max-h-[60vh] w-auto rounded-[6px] object-contain"
              />
            ) : (
              <p className="py-12 text-body-m text-[var(--text-content-sub-title)]">
                {t('contentLibrary.previewUnavailable')}
              </p>
            ))}

          {item.mediaType === ContentMediaType.VIDEO &&
            (item.videoProcessingStatus === VideoProcessingStatus.READY &&
            item.videoPlaybackUrl ? (
              <TeeVideoPlayer
                src={item.videoPlaybackUrl}
                poster={item.videoThumbnailUrl}
                className="max-h-[60vh] w-full rounded-[6px]"
                errorLabel={t('contentLibrary.previewPlaybackError')}
                autoPlay
              />
            ) : (
              <p className="py-12 text-body-m text-[var(--text-content-sub-title)]">
                {t('contentLibrary.previewVideoProcessing')}
              </p>
            ))}

          {item.mediaType === ContentMediaType.EMBED &&
            (embedIframeSrc ? (
              <iframe
                src={embedIframeSrc}
                title={item.title}
                className="aspect-video w-full max-h-[60vh] rounded-[6px]"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            ) : item.embedUrl ? (
              <div className="flex flex-col items-center gap-4 py-12">
                <p className="text-body-m text-[var(--text-content-sub-title)]">
                  {t('contentLibrary.previewEmbedHint')}
                </p>
                <TeeButton
                  variant="secondary"
                  text={t('contentLibrary.previewOpenExternal')}
                  rightIcon={<IconExternalLink size={16} />}
                  onClick={() => window.open(item.embedUrl, '_blank', 'noopener,noreferrer')}
                />
              </div>
            ) : (
              <p className="py-12 text-body-m text-[var(--text-content-sub-title)]">
                {t('contentLibrary.previewUnavailable')}
              </p>
            ))}
        </div>
      </div>
    </div>
  );
}
